// Servicio de encriptación para stepperData
class EncryptionService {
  constructor() {
    // Clave de encriptación (en producción debería estar en variables de entorno)
    this.secretKey = process.env.VUE_APP_ENCRYPTION_KEY || 'pdv-stepper-2024-secret-key';
    
    // Habilitar/deshabilitar encriptación desde variables de entorno
    this.encryptionEnabled = process.env.VUE_APP_ENABLE_ENCRYPTION !== 'false';
  }

  // Encriptar un valor
  encrypt(value) {
    if (!value) return value;
    
    // Si la encriptación está deshabilitada, devolver el valor original
    if (!this.encryptionEnabled) {
      return value;
    }
    
    try {
      // Convertir a string si es un objeto
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      
      // Encriptación simple usando base64 + XOR (para demostración)
      // En producción usarías una librería como crypto-js
      let encrypted = '';
      for (let i = 0; i < stringValue.length; i++) {
        const charCode = stringValue.charCodeAt(i) ^ this.secretKey.charCodeAt(i % this.secretKey.length);
        encrypted += String.fromCharCode(charCode);
      }
      
      return btoa(encrypted);
    } catch (error) {
      console.error('Error encriptando:', error);
      return value;
    }
  }

  // Desencriptar un valor
  decrypt(encryptedValue) {
    if (!encryptedValue) return encryptedValue;
    
    // Si la encriptación está deshabilitada, devolver el valor original
    if (!this.encryptionEnabled) {
      return encryptedValue;
    }
    
    try {
      // Desencriptación inversa
      const decoded = atob(encryptedValue);
      let decrypted = '';
      
      for (let i = 0; i < decoded.length; i++) {
        const charCode = decoded.charCodeAt(i) ^ this.secretKey.charCodeAt(i % this.secretKey.length);
        decrypted += String.fromCharCode(charCode);
      }
      
      // Intentar parsear como JSON si es posible
      try {
        return JSON.parse(decrypted);
      } catch {
        return decrypted;
      }
    } catch (error) {
      console.error('Error desencriptando:', error);
      return encryptedValue;
    }
  }

  // Encriptar un objeto completo
  encryptObject(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    
    const encrypted = {};
    for (const [key, value] of Object.entries(obj)) {
      encrypted[key] = this.encrypt(value);
    }
    return encrypted;
  }

  // Desencriptar un objeto completo
  decryptObject(encryptedObj) {
    if (!encryptedObj || typeof encryptedObj !== 'object') return encryptedObj;
    
    const decrypted = {};
    for (const [key, value] of Object.entries(encryptedObj)) {
      decrypted[key] = this.decrypt(value);
    }
    return decrypted;
  }

  // Obtener valor de un campo específico (encriptado o desencriptado)
  getFieldValue(data, fieldPath, encrypted = false) {
    if (!data) return null;
    
    const keys = fieldPath.split('.');
    let value = data;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }
    
    return encrypted ? value : this.decrypt(value);
  }

  // Establecer valor de un campo específico (encriptado)
  setFieldValue(data, fieldPath, value) {
    if (!data) return data;
    
    const keys = fieldPath.split('.');
    const lastKey = keys.pop();
    let current = data;
    
    for (const key of keys) {
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }
    
    current[lastKey] = this.encrypt(value);
    return data;
  }
}

export default new EncryptionService();
